/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ErrorResponseModel, NotificationModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    UtilsService,
    ValidationService
} from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { TemplateModel } from '../../../models';
import { WizardService, WizardTemplateService } from '../../../services';
import { WizardTitleEnum } from '../../../enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-template-selector',
    templateUrl: './templateSelector.component.html',
    styleUrls: ['./templateSelector.component.scss'],
})

export class TemplateSelectorComponent extends BaseComponent implements OnInit {
    /**
     * Access the input for file type.
     * @private
     */
    @ViewChild('inputFile') private inputFile: any;

    /**
     * Stored available templates.
     */
    @Input() set templates(templates: any[]) {
        this._templates = this.utilsService.cleanArray(templates);
    }

    /**
     * Cancel button text.
     */
    @Input() public cancelButtonText: string;

    /**
     * Emit complete to parent component.
     */
    @Output() public templateSelected: EventEmitter<any> = new EventEmitter();

    /**
     * Emit cancel event to parent.
     */
    @Output() public exit: EventEmitter<any> = new EventEmitter();

    /**
     * Form for storing template values.
     */
    public templateForm: FormGroup;

    /**
     * There is a user template loaded.
     */
    public userTemplateLoaded: boolean = false;

    /**
     * Selected template state used for styling and passing to wizard.
     */
    public erroredTemplate: string;

    /**
     * To store the error message.
     */
    public errorMessages: string[];

    /**
     * To store the selected templates.
     */
    public selectedTemplate: any = [];

    /**
     * Determine if the option to upload a template has been selected.
     */
    public importTemplateSelected: boolean = false;

    /**
     * Stored description for the selected template, stops loopy code being called from the view.
     */
    public description: string;

    /**
     * Error notification.
     */
    public errorNotification: NotificationModel;

    /**
     * Selected template state used for styling and passing to wizard.
     * @private
     */
    private _selectedTitle: string;

    /**
     * Store the templates so we can clean out any null values as is the case for adding a GSLB service.
     * @private
     */
    private _templates: any[] = [];

    constructor(
        public navigationService: NavigationService,
        public utilsService: UtilsService,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private validationService: ValidationService,
        private wizardService: WizardService,
        private wizardTemplateService: WizardTemplateService
    ) {
        super();

        this.setUpFormGroup();
        this.setUpSubscribers();
    }

    /**
     * Templates getter.
     */
    get templates(): any[] {
        return this._templates;
    }

    /**
     * Get selected title.
     */
    get selectedTitle(): string {
        return this._selectedTitle;
    }

    /**
     * Set selected title.
     * @param templateTitle
     */
    set selectedTitle(templateTitle: string) {
        this._selectedTitle = templateTitle;
    }

    ngOnInit(): void {
        this.setupTemplateIdsForView();
        this.setSelectedService();
        // Pre select template
        let template: TemplateModel;
        if (this.templates.length === 1) {
            template = this.templates[0];
        } else {
            template = this.wizardService.selectedTemplate ||
                this.templates.find((item: TemplateModel) => item.title === 'Layer 7'); // <-- Default template
        }

        if (template) {
            this.setSelectedTemplate(template);
        }
        this.wizardService.isLoading = false;
    }

    /**
     * Check to see if the user has selected import.
     * @param template
     */
    public setSelectedTemplate(template: any | string): void {
        if (template.disabled) {
            return;
        }

        // @ts-ignore
        if (template !== 'import') {
            this.importTemplateSelected = false;
            this.selectedTemplate = template;
            this.description = this.wizardTemplateService.formatTemplateDescription(
                template, { service: WizardTitleEnum.TEMPLATE }
            );
        } else {
            this.importTemplateSelected = true;
            this.selectedTemplate = { title: template };
            this.inputFile.nativeElement.click();
        }

        this.checkForErrors(this.selectedTemplate);
    }

    /**
     * Close error notification emitter
     * @param notificationId
     */
    public closeErrorNotification(notificationId: string) {
        this.wizardService.errorNotificationClosed.next(true);
        this.notifyService.removeNotificationById(notificationId);
    }

    /**
     * Capture the file change event.
     * @param event
     */
    public onFileChange(event: any): void {
        this.logService.debug('onFileChange', 'start');
        const file = event.target.files[0];

        if (this.failedToGetFileFromBrowser(event) || this.checkForJSONFile(file)) {
            return;
        }

        const reader = new FileReader();
        // Set up a callback for when the file is loaded
        reader.addEventListener('load', $event => this.onReaderLoad($event, file.name));
        reader.readAsText(event.target.files[0]);
    }

    /**
     * File loaded callback function.
     * @param event
     * @param fileName
     */
    public onReaderLoad(event: any, fileName: string): void {
        const file = this.validationService.isJson(event.target.result);
        if (file.error) {
            const errorMessage = 'This file is invalid JSON' + file.error;
            this.logService.error('isJSONValid', errorMessage);
            this.notifyService.error(errorMessage, { id: 'wizardError', wizardError: 'wizardError' });
        } else if (file.json) {
            this.notifyService.removeNotificationById('wizardError');
            const json = file.json;
            json.isUserTemplate = true;
            json.fileName = fileName;
            this.addTemplateToService(json);
            this.setPageState(json);
        }

        return;
    }

    /**
     * Remove template helper function.
     * @param template
     */
    public checkTemplateBeforeRemoval(template: TemplateModel): void {
        if (this.wizardService.selectedTemplate !== template) {
            this.removeUserTemplate();
            this.removeTemplateFromArray(template);
            // Reset the input value to allow user to upload the same file again.
            this.inputFile.nativeElement.value = '';
        } else {
            this.templateForm.controls['title'].setValue('');
            this.runValidationCheck(template);
        }
    }

    /**
     * Select the template in the form and confirm next.
     * @param selectedTitle
     */
    public selectTemplate(selectedTitle: string): void {
        this.templateForm.get('title').setValue(selectedTitle);
        this.confirmNext(
            this.wizardService.selectedTemplate === null || this.wizardService.selectedTemplate.title === selectedTitle
        );
    }

    /**
     * Return to parent page.
     */
    public exitWizard(): void {
        this.exit.emit(true);
    }

    /**
     * Give each template an ID for the view.
     * @private
     */
    private setupTemplateIdsForView(): void {
        for (const template of this.templates) {
            template['htmlId'] = this.utilsService.convertStringToId(template.title);
        }
    }

    /**
     * Check for any template errors.
     * @param selectedTemplate
     * @param formData
     * @private
     */
    private checkForErrors(selectedTemplate: TemplateModel, formData?: any): void {
        const error = this.getSelectedTemplateErrors(selectedTemplate);
        if (error && error.length) {
            this.erroredTemplate = formData ? formData.title : selectedTemplate.title;
            this.errorMessages = error;
        } else {
            this.erroredTemplate = '';
            this.errorMessages = [];
        }
        this.runValidationCheck();
    }

    /**
     * Get errors form the selected template.
     * @param selectedTemplate
     * @private
     */
    private getSelectedTemplateErrors(selectedTemplate: TemplateModel): any {
        const error: any = [];
        if (selectedTemplate.type === 'layer4') {
            return error.concat(this.wizardTemplateService.validateSelectedTemplate(selectedTemplate));
        }

        if (selectedTemplate.type === 'layer7' || selectedTemplate.type === 'monitor') {
            if (selectedTemplate.type === 'monitor') {
                // Here we get the existing monitor from the templates list to find if it has a disabled message.
                // The only reason a monitor template would be disabled is if there are no http backends which would be
                // the same for a user template.
                const monitorTemplate = this.templates.find((item: TemplateModel) => item.title === 'L7 Monitor');
                if (monitorTemplate && monitorTemplate.disabled) {
                    error.push(monitorTemplate.disabledMessage);
                }
            }

            return error.concat(this.wizardTemplateService.validateSelectedTemplate(selectedTemplate));
        }

        if (selectedTemplate.type === 'GSLB') {
            // TODO switch statement for other type of service or try and move this into the new service modal.
        }
    }

    /**
     * Helper function to add file contents to lb service.
     * @param template
     * @private
     */
    private addTemplateToService(template: any): void {
        this.templates.push(template);
        this.selectedTemplate = template;
        this.importTemplateSelected = false;
        this.description = this.wizardTemplateService.formatTemplateDescription(
            template, { service: WizardTitleEnum.TEMPLATE }
        );
    }

    /**
     * Sets page state so view is correct (expects json template).
     * @param template
     * @private
     */
    private setPageState(template: any): void {
        this.userTemplateLoaded = true;
        this.templateForm.get('title').setValue(template.title);
        this.runValidationCheck();
    }

    /**
     * Runs validation checks and updates the wizard service when page is valid.
     * @param templateToRemove
     * @private
     */
    private runValidationCheck(templateToRemove?: TemplateModel): void {
        const selectedItem = this.templateForm.value;
        const selectedTemplate = this.templates.find((template: TemplateModel) => template.title === selectedItem.title);
        this.handleTemplateChanges(templateToRemove, selectedTemplate, selectedItem);
    }

    /**
     * Handle template changes.
     * @param templateToRemove
     * @param selectedTemplate
     * @param selectedItem
     * @private
     */
    private handleTemplateChanges(templateToRemove: TemplateModel, selectedTemplate: TemplateModel, selectedItem: any) {
        if (this.wizardService.selectedTemplate && (this.wizardService.selectedTemplate !== selectedTemplate)) {
            this.confirmTemplateChange(templateToRemove, selectedItem);
        }
    }

    /**
     * Confirm template change.
     * @param templateToRemove
     * @param selectedItem
     * @private
     */
    private confirmTemplateChange(templateToRemove: TemplateModel, selectedItem: any): void {
        const confirmationObject = {
            title: 'wizard_change_template',
            message: 'wizard_change_template_text',
            callBack: this.changeTemplate.bind(this, templateToRemove, selectedItem),
            options: { translateText: true }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Change the template.
     * @param templateToRemove
     * @param selectedItem
     * @private
     */
    private changeTemplate(templateToRemove: TemplateModel, selectedItem: any): Observable<any> {
        return new Observable((observer) => {
            this.removeTemplate(templateToRemove);
            this.wizardService.resetWizardService();
            this.wizardService.createTemplateSelectionStep();
            this.wizardService.isLoading = false;
            this.templateForm.get('title').setValue(selectedItem.title);
            if (templateToRemove.isUserTemplate) {
                this.removeUserTemplate();
            }
            this.selectedTitle = selectedItem.title;
            this.confirmNext(this.selectedTitle !== '');
            observer.next();
            observer.complete();
        });
    }

    /**
     * Set variables associated to user templates.
     */
    private removeUserTemplate(): void {
        this.userTemplateLoaded = false;
        this.selectedTemplate.title = 'import';
        this.importTemplateSelected = true;
    }

    /**
     * Check to see if there is a selected service.
     * @private
     */
    private setSelectedService(): void {
        const title = this.wizardService.selectedTemplate && this.wizardService.selectedTemplate.title;
        if (title) {
            this.selectedTitle = title;
            this.templateForm.patchValue({ title });
            this.wizardService.isSectionValidEmitter.next(true);
        }
        if (this.templates.some((template: TemplateModel) => template.isUserTemplate)) {
            this.userTemplateLoaded = true;
        }
    }

    /**
     * Set up the template form.
     * @private
     */
    private setUpFormGroup(): void {
        this.templateForm = this.formBuilder.group({
            title: [null, Validators.required]
        });
    }

    /**
     * Set up the form subscriber.
     * @private
     */
    private setUpSubscribers(): void {
        this.templateForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (formData: any): void => {
                    if (formData && formData.title) {
                        this.selectedTitle = formData.title;
                        const selectedTemplate = this.templates.find((template: TemplateModel) => template.title === formData.title);
                        this.checkForErrors(selectedTemplate, formData);
                        this.wizardTemplateService.templateChangeEmitter.next(
                            this.errorMessages.length ? null : selectedTemplate
                        );
                    }
                }
            });

        this.modalService.cancelSubmission
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (closed: boolean): void => {
                    if (closed) {
                        this.selectedTemplate = this.wizardService.selectedTemplate;
                        const title = this.wizardService.selectedTemplate.title;
                        this.templateForm.get('title').setValue(title);
                        this.selectedTitle = title;
                    }
                }
            });

        this.notifyService.updateNotificationsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (notifications: NotificationModel[]): void => {
                    const wizardError = notifications.find(item => item.type === 'wizardError');
                    if (wizardError) {
                        wizardError['class'] = this.getLevelClassName(wizardError.level);
                        this.errorNotification = wizardError;
                    } else {
                        this.errorNotification = null;
                    }
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.warn('NotificationWrapperComponent: ', error.message);
                }
            });
    }

    /**
     * Gets notifications class name from the level.
     * @param level
     * @private
     */
    private getLevelClassName(level: number): string {
        return ({
            5: 'debug',
            4: 'information',
            3: 'success',
            2: 'warning',
            1: 'error'
        })[level];
    }

    /**
     * Check if it failed to get the import from the browser.
     * @param event
     * @private
     */
    private failedToGetFileFromBrowser(event: any): boolean {
        const fileList: FileList = event.target.files;
        if (!fileList.length) {
            this.logService.error(
                'TemplateSelectorComponent: onFileChange',
                'No file retrieved from the browser event'
            );

            return !fileList.length;
        }
    }

    /**
     * Check the import is a JSON file.
     * @param file
     * @private
     */
    private checkForJSONFile(file: any): boolean {
        if (file.type !== 'application/json' && file.type !== '') {
            this.notifyService.error(
                'This file is invalid JSON ' + file.error,
                { id: 'wizardError', wizardError: 'wizardError' });

            return file.type !== 'application/json' && file.type !== '';
        }
    }

    /**
     * Remove the given template from the array.
     * @param template
     * @private
     */
    private removeTemplateFromArray(template: any): void {
        const deleteIndex = this.templates.findIndex((storedTemplate: TemplateModel) => storedTemplate.title === template.title);
        if (deleteIndex !== -1) {
            this.templates.splice(deleteIndex, 1);
        }
    }

    /**
     * Confirm for the wizard to move to the next component.
     * @param condition
     * @private
     */
    private confirmNext(condition: boolean): void {
        if (condition) {
            this.templateSelected.next(true);
        }
    }

    /**
     * Check the selected template after confirming form data change.
     * @param templateToRemove
     * @private
     */
    private removeTemplate(templateToRemove: TemplateModel): void {
        if (!templateToRemove) {
            return;
        }
        this.removeTemplateFromArray(templateToRemove);
        this.userTemplateLoaded = false;
        this.importTemplateSelected = true;
    }
}
